import {computed, inject, Injectable, signal} from '@angular/core';
import {
  CreatePluginRequest,
  PluginDetails,
  PluginService,
  PluginSummary,
  UpdatePluginRequest,
} from '../../../../api';
import {lastValueFrom} from 'rxjs';
import {injectMutation, injectQuery, QueryClient} from '@tanstack/angular-query-experimental';
import {QueryKey} from '../../../constants/query-key';

@Injectable()
export class PluginStoreService {
  private readonly pluginService = inject(PluginService);
  private readonly queryClient = inject(QueryClient);

  selectedPluginId = signal<number | null>(null);
  searchTerm = signal<string | null>(null);

  private readonly pluginsQuery = injectQuery(() => ({
    queryKey: [QueryKey.PLUGINS, this.searchTerm()],
    queryFn: () => lastValueFrom(this.pluginService.getPlugins(this.searchTerm() ?? '')),
  }));

  plugins = computed<PluginSummary[]>(() => this.pluginsQuery.data()?.plugins ?? []);
  isLoading = computed(() => this.pluginsQuery.isLoading());

  selectedPluginQuery = injectQuery(() => ({
    queryKey: [QueryKey.PLUGIN, this.selectedPluginId()],
    queryFn: () => lastValueFrom(this.pluginService.getPluginById(this.selectedPluginId()!)),
    enabled: this.selectedPluginId() !== null,
    staleTime: 0,
  }));
  selectedPlugin = computed<PluginDetails | null>(() => {
    if (this.selectedPluginId() === null) return null;
    return this.selectedPluginQuery.data()?.plugin ?? null;
  });
  isLoadingSelected = computed(() => this.selectedPluginQuery.isLoading());

  createPluginMutation = injectMutation(() => ({
    mutationFn: (request: CreatePluginRequest) =>
      lastValueFrom(this.pluginService.createPlugin(request)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PLUGINS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PLUGIN_SELECT]});
    },
  }));

  updatePluginMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; body: UpdatePluginRequest }) =>
      lastValueFrom(this.pluginService.updatePlugin(request.id, request.body)),
    onSuccess: (_, request) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PLUGINS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PLUGIN, request.id]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PLUGIN_SELECT]});
    },
  }));

  deletePluginMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.pluginService.deletePlugin(id)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PLUGINS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PLUGIN_SELECT]});
    },
  }));
}
